import { createUseStyles } from 'react-jss';
import color from 'color';

import { ThemeProps } from '@styles/theme';

import { flex, textOverflowDots, customScroll } from '@resources/mixins';

export const getCustomStyles = (themeColors: ThemeProps['colors']) => ({
    menuPortal: (provided: object) => ({
        ...provided,
        zIndex: 1500,
    }),
    menu: (provided: object) => ({
        ...provided,
        boxShadow: 'none',
        zIndex: 1000,
        marginTop: 1,
        border: `1.5px solid ${themeColors.violet_primary}`,
        backgroundColor: themeColors.white,
        minWidth: 280,

        '& > div': {
            maxHeight: 400,
            ...customScroll(),
        },
    }),
    singleValue: (provided: object) => ({
        ...provided,
        fontSize: 16,
        color: themeColors.black,
    }),
    indicatorsContainer: (provided: object) => ({
        ...provided,
        alignItems: 'flex-start',
    }),
    dropdownIndicator: (provided: object, state: Record<string, any>) => ({
        ...provided,
        color: themeColors.violet_primary,
        transition: 'transform .2s ease',
        transformOrigin: 'center',
        transform: state.selectProps.menuIsOpen ? 'scale(1, -1)' : 'scale(1, 1)',
    }),
    indicatorSeparator: (provided: object) => ({
        ...provided,
        display: 'none',
    }),
    control: (provided: object, state: Record<string, any>) => {
        const { error } = state.selectProps;

        return ({
            ...provided,
            borderWidth: 1.5,
            borderColor: error
                ? themeColors.red
                : state.isFocused
                    ? themeColors.violet_primary
                    : themeColors.grey_light,
            backgroundColor: themeColors.white,
            boxShadow: 'none',
            maxHeight: 150,
            ...customScroll(),

            '&:hover': {
                borderColor: `${color(themeColors.violet_primary).fade(0.62)}`,
            },
        })
    },
    option: (provided: object, state: Record<string, any>) => ({
        ...provided,
        backgroundColor: state.isSelected
            ? color(themeColors.violet_primary).fade(0.85).string()
            : state.isFocused
                ? color(themeColors.violet_primary).fade(0.92).string()
                : themeColors.white,
        color: themeColors.black,
    }),
    input: (provided: object) => ({
        ...provided,
        color: themeColors.black,
    }),
});

export const useStyles = createUseStyles((theme: ThemeProps) => ({
    expanded: {},
    notActive: {},

    button: {
        border: 'none',
        background: 'none',
        outline: 'none',
        padding: 0,
        height: 40,
        width: 40,
        ...flex('row', 'center', 'center'),

        '&:disabled': {
            opacity: 0.3,
        },

        '&:hover': {
            background: theme.colors.icon_background,
            borderRadius: theme.radius / 2,
            cursor: 'pointer',
        },
        [theme.down('desktop')]: {
            height: 32,
            width: 32,
        },
    },

    nameWrapper: {
        ...flex('row', 'flex-start', 'center'),
        minWidth: 0,
    },

    procedureImage: {
        width: 40,
        height: 40,
        objectFit: 'cover',
        marginRight: theme.spacing(3),
        borderRadius: theme.radius,
        backgroundColor: theme.colors.grey_light,
        ...flex('row', 'center', 'center'),
        flexShrink: 0,
    },

    moreItemsPresent: {
        // color: theme.colors.blue_link,
        // marginLeft: theme.spacing(2),
        // fontFamily: theme.font.secondary,
    },

    noInfo: {
        color: theme.colors.grey_text,
        ...textOverflowDots(),
        display: 'inherit',
    },

    capitalized: {
        textTransform: 'capitalize',
    },

    ellipsis: {
        ...textOverflowDots(),
        zIndex: 1000,
    },

    procedureName: {
        ...textOverflowDots(),
        fontSize: 13,
        whiteSpace: 'normal',
    },

    searchPanel: {
        flex: '0 1 220px',
        maxWidth: 220,
        paddingRight: 0,
        minWidth: 160,
        [theme.down('desktop')]: {
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            paddingRight: 0,
        },
        [theme.down('mobile')]: {
            width: 'auto',
            flex: 1,
        },
    },

    // Hidden on phone: the Users/Procedures toggle already labels the list.
    sectionTitle: {
        [theme.down('mobile')]: {
            display: 'none',
        },
    },

    mainWrapper: {
        padding: theme.spacing(2),
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 0,
        [theme.down('desktop')]: {
            width: '100%',
            height: 'auto',
        },
    },

    headerWrapper: {
        minHeight: 56,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        padding: 0,
        gap: theme.spacing(2),
        flexWrap: 'nowrap',
        [theme.down('desktop')]: {
            minHeight: 0,
            flexWrap: 'wrap',
            alignItems: 'stretch',
            padding: 0,
            marginBottom: theme.spacing(3),
        },
        [theme.down('mobile')]: {
            flexWrap: 'nowrap',
            alignItems: 'center',
        },
    },

    titleWrapper: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        gap: theme.spacing(2),
        flex: 1,
        [theme.down('desktop')]: {
            width: '100%',
            flexWrap: 'wrap',
        },
        [theme.down('mobile')]: {
            width: 'auto',
            flex: '0 0 auto',
            flexWrap: 'nowrap',
        },
    },

    assignedCounter: {
        minHeight: '30px',
        minWidth: '200px',
        background: theme.colors.surface_elevated,
        border: `1px solid ${theme.colors.grey_light}`,
        borderRadius: '5px',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-evenly',
        alignContent: 'center',
        padding: '4px 10px',
        color: theme.colors.black,
        fontFamily: theme.font.secondary,
        fontSize: 14,
        [theme.down('desktop')]: {
            marginLeft: 0,
        },
    },

    tableWrapper: {
        flex: 1,
        minHeight: 0,
        width: '100%',
        overflow: 'hidden',
    },

    typeBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 44,
        padding: `${theme.spacing(0.75)} ${theme.spacing(1.5)}`,
        borderRadius: theme.radius,
        color: theme.colors.violet_primary,
        backgroundColor: `${color(theme.colors.violet_primary).fade(0.92)}`,
        fontFamily: theme.font.secondary,
        fontSize: 14,
        lineHeight: 1.2,
        fontWeight: 'bold',
    },

    versionSelectWrapper: {
        width: 116,
        maxWidth: '100%',
        position: 'relative',
        zIndex: 2,
    },

    versionOption: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        width: '100%',
        padding: '2px 0',
    },
    versionOptionLabel: {
        fontSize: 13,
        fontWeight: 500,
        color: theme.colors.black,
        flexShrink: 0,
        minWidth: 40,
    },
    versionMeta: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 4,
        justifyContent: 'flex-end',
    },
    versionDate: {
        fontSize: 11,
        color: theme.colors.grey_text,
        whiteSpace: 'nowrap',
        marginRight: 2,
    },
    versionMetaChip: {
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.04em',
        color: theme.colors.violet_primary,
        background: color(theme.colors.violet_primary).fade(0.9).string(),
        borderRadius: 4,
        padding: '1px 5px',
        whiteSpace: 'nowrap',
        textTransform: 'uppercase' as const,
    },

    actionsWrapper: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: theme.spacing(0.5),
        minWidth: 80,
    },

    select: {
        width: '100%',
        '&:focus': {
            outline: 'none',
        },
        [theme.down('desktop')]: {
            minWidth: 104,
        },
    },
}));
